import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Modal, ModalBody } from 'reactstrap';
import CloseIcon from "@material-ui/icons/Close";
import SwapHorizIcon from '@material-ui/icons/SwapHoriz';
import PanToolIcon from '@material-ui/icons/PanTool';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import CancelIcon from '@material-ui/icons/Cancel';
import NotificationsIcon from '@material-ui/icons/Notifications';
import WarningIcon from '@material-ui/icons/Warning';
import CheckIcon from '@material-ui/icons/Check';
import HourglassEmptyIcon from "@material-ui/icons/HourglassEmpty";

import { closeNotification } from "../../../actions/notificationActions";
import { approveShiftSwap, rejectShiftSwap, clearSwapMessage } from "../../../actions/shiftSwapActions";

const OneNotificationModal = () => {
    const dispatch = useDispatch();
    
    // Selectors
    const showNotification = useSelector(state => state.notificationData.openNotification);
    const notification = useSelector(state => state.notificationData.oneNotification) || {};
    const swapState = useSelector(state => state.shiftSwapData || {});

    const [showRejectInput, setShowRejectInput] = useState(false);
    const [rejectionReason, setRejectionReason] = useState('');

    // Handle success/error from Redux
    useEffect(() => {
        if (swapState.approveSuccess || swapState.rejectSuccess) {
            // show toast briefly then close & clear
            const t = setTimeout(() => {
                toggleModal();
                dispatch(clearSwapMessage());
                // preserve previous behavior; you may remove reload if you prefer
                window.location.reload();
            }, 1500);

            return () => clearTimeout(t);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [swapState.approveSuccess, swapState.rejectSuccess]);

    const toggleModal = () => {
        dispatch(closeNotification());
        dispatch(clearSwapMessage());
        setShowRejectInput(false);
        setRejectionReason('');
    };

    const getCompanyId = () => {
        return sessionStorage.getItem('companyUserId') || 
               sessionStorage.getItem('userId') || 
               localStorage.getItem('companyId');
    };

    const getCurrentUser = () => {
        const userStr = sessionStorage.getItem('user') || localStorage.getItem('user');
        if (userStr) {
            try {
                return JSON.parse(userStr);
            } catch (e) {
                return null;
            }
        }
        return null;
    };

    // ============================================================================
    // APPROVE SHIFT SWAP
    // ============================================================================
    const handleApproveSwap = () => {
        if (!notification?.visitId) {
            alert('⚠️ Visit ID not found in notification');
            return;
        }

        if (!window.confirm('Are you sure you want to approve this shift swap?')) {
            return;
        }

        const currentUser = getCurrentUser();
        const companyId = getCompanyId();

        const swapData = {
            visitId: notification.visitId, // pass visitId; server/action will fetch swapId if needed
            companyId,
            adminId: currentUser?._id || currentUser?.id,
            adminName: currentUser?.name || (currentUser?.firstname ? `${currentUser.firstname} ${currentUser.lastname}`.trim() : 'Admin'),
            approvalNotes: 'Approved from notification'
        };

        console.log('Approving swap with data:', swapData);
        dispatch(approveShiftSwap(swapData));
    };

    // ============================================================================
    // REJECT SHIFT SWAP
    // ============================================================================
    const handleRejectSwap = () => {
        if (!notification?.visitId) {
            alert('⚠️ Visit ID not found in notification');
            return;
        }

        if (!rejectionReason.trim()) {
            alert('⚠️ Please provide a reason for rejection');
            return;
        }

        if (rejectionReason.trim().length < 10) {
            alert('⚠️ Rejection reason must be at least 10 characters');
            return;
        }

        const currentUser = getCurrentUser();
        const companyId = getCompanyId();

        const swapData = {
            visitId: notification.visitId,
            companyId,
            adminId: currentUser?._id || currentUser?.id,
            adminName: currentUser?.name || (currentUser?.firstname ? `${currentUser.firstname} ${currentUser.lastname}`.trim() : 'Admin'),
            rejectionReason: rejectionReason.trim()
        };

        console.log('Rejecting swap with data:', swapData);
        dispatch(rejectShiftSwap(swapData));
    };

    // Notification icon using Material UI icons
    const getNotificationIcon = (category) => {
        switch (category) {
            case 'shift_swap_request':
                return <SwapHorizIcon style={{ color: '#2563EB', marginRight: 8 }} />;
            case 'shift_swap_claimed':
                return <PanToolIcon style={{ color: '#D97706', marginRight: 8 }} />;
            case 'shift_swap_approved':
                return <CheckCircleIcon style={{ color: '#10B981', marginRight: 8 }} />;
            case 'shift_swap_rejected':
                return <CancelIcon style={{ color: '#EF4444', marginRight: 8 }} />;
            default:
                return <NotificationsIcon style={{ color: '#6B7280', marginRight: 8 }} />;
        }
    };

    const isSwapNotification = Boolean(notification?.category && notification.category.includes('shift_swap'));
    const isSwapRequest = notification?.category === 'shift_swap_request';
    const isSwapClaimed = notification?.category === 'shift_swap_claimed';
    const needsApproval = (isSwapRequest || isSwapClaimed) && Boolean(notification?.visitId);

    return (
        <>
            <Modal 
                isOpen={Boolean(showNotification)} 
                toggle={toggleModal} 
                backdrop="static" 
                className="one-notification-modal"
            >
                <div className="notification-header" style={{ display: 'flex', alignItems: 'center', padding: '1rem', borderBottom: '1px solid #eee' }}>
                    <div style={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                        <span className="notification-icon">{getNotificationIcon(notification?.category)}</span>
                        <span className="notification-title" style={{ fontWeight: 600 }}>{notification?.title || 'Notification'}</span>
                    </div>

                    <button
                        className='notifiy-close'
                        onClick={toggleModal}
                        aria-label="Close notification"
                        style={{
                            background: 'transparent',
                            border: 'none',
                            cursor: 'pointer',
                            padding: 8,
                            display: 'flex',
                            alignItems: 'center'
                        }}
                    >
                        <CloseIcon />
                    </button>
                </div>
                
                <ModalBody className="one-notification-body" style={{ padding: '1rem' }}>
                    {/* Success/Error Message from Redux */}
                    {swapState.message && (
                        <div className={`notification-toast ${swapState.approveSuccess || swapState.rejectSuccess ? 'success' : 'error'}`} style={{ marginBottom: 12 }}>
                            {swapState.message}
                        </div>
                    )}

                    {swapState.error && (
                        <div className="notification-toast error" style={{ color: '#EF4444', display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                            <WarningIcon style={{ color: '#EF4444' }} />
                            <span>{swapState.error}</span>
                        </div>
                    )}

                    {/* Needs Action Badge */}
                    {isSwapNotification && needsApproval && (
                        <div className="notification-badge needs-action" style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '6px 10px', borderRadius: 6, background: '#FFF7ED', color: '#92400E', marginBottom: 12 }}>
                            <WarningIcon style={{ color: '#F59E0B' }} />
                            <span style={{ fontWeight: 600 }}>Action Required</span>
                        </div>
                    )}
                    
                    {/* Notification Content */}
                    <div className="notification-description" style={{ marginTop: 12, marginBottom: 16 }}>
                        {notification?.content || 'No content available.'}
                    </div>
                    
                    {/* APPROVE/REJECT ACTIONS */}
                    {needsApproval && !showRejectInput && (
                        <div className="notification-actions-swap" style={{ display: 'flex', gap: 12, marginBottom: 16 }}>
                            <button 
                                className='btn-approve-swap'
                                onClick={handleApproveSwap}
                                disabled={swapState.loading}
                                style={{
                                    flex: 1,
                                    padding: '0.85rem 1rem',
                                    border: 'none',
                                    borderRadius: 6,
                                    fontWeight: 600,
                                    fontSize: '0.95rem',
                                    cursor: swapState.loading ? 'not-allowed' : 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    gap: 8,
                                    background: '#10B981',
                                    color: '#fff'
                                }}
                            >
                                {swapState.loading ? (
                                    <>
                                        <HourglassEmptyIcon fontSize="small" /> Approving...
                                    </>
                                ) : (
                                    <>
                                        <CheckIcon fontSize="small" /> Approve Swap
                                    </>
                                )}
                            </button>
                            
                            <button 
                                className='btn-reject-swap'
                                onClick={() => setShowRejectInput(true)}
                                disabled={swapState.loading}
                                style={{
                                    flex: 1,
                                    padding: '0.85rem 1rem',
                                    border: 'none',
                                    borderRadius: 6,
                                    fontWeight: 600,
                                    fontSize: '0.95rem',
                                    cursor: swapState.loading ? 'not-allowed' : 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    gap: 8,
                                    background: '#EF4444',
                                    color: '#fff'
                                }}
                            >
                                <CancelIcon fontSize="small" /> Reject Swap
                            </button>
                        </div>
                    )}

                    {/* REJECTION REASON INPUT */}
                    {showRejectInput && (
                        <div className="rejection-input-container" style={{ marginTop: 8 }}>
                            <label className="rejection-label" style={{ display: 'block', marginBottom: 8, fontWeight: 600 }}>
                                Rejection Reason <span style={{ color: '#EF4444' }}>*</span>
                            </label>
                            <textarea
                                className="rejection-textarea"
                                value={rejectionReason}
                                onChange={(e) => setRejectionReason(e.target.value)}
                                placeholder="Please provide a detailed reason for rejecting this swap (minimum 10 characters)..."
                                rows="4"
                                disabled={swapState.loading}
                                style={{
                                    width: '100%',
                                    padding: 10,
                                    borderRadius: 6,
                                    border: '1px solid #E5E7EB',
                                    resize: 'vertical',
                                    fontSize: 14,
                                    marginBottom: 8
                                }}
                            />
                            <div className="char-count" style={{ fontSize: 13, color: '#6B7280', marginBottom: 8 }}>
                                {rejectionReason.length} / 10 minimum characters
                            </div>
                            
                            <div className="rejection-actions" style={{ display: 'flex', gap: 8 }}>
                                <button 
                                    className='btn-cancel-reject'
                                    onClick={() => {
                                        setShowRejectInput(false);
                                        setRejectionReason('');
                                    }}
                                    disabled={swapState.loading}
                                    style={{
                                        flex: 1,
                                        padding: '0.6rem 0.8rem',
                                        border: '1px solid #E5E7EB',
                                        borderRadius: 6,
                                        background: '#fff',
                                        cursor: swapState.loading ? 'not-allowed' : 'pointer'
                                    }}
                                >
                                    Cancel
                                </button>
                                <button 
                                    className='btn-confirm-reject'
                                    onClick={handleRejectSwap}
                                    disabled={swapState.loading || rejectionReason.trim().length < 10}
                                    style={{
                                        flex: 1,
                                        padding: '0.6rem 0.8rem',
                                        border: 'none',
                                        borderRadius: 6,
                                        background: '#EF4444',
                                        color: '#fff',
                                        cursor: (swapState.loading || rejectionReason.trim().length < 10) ? 'not-allowed' : 'pointer'
                                    }}
                                >
                                    {swapState.loading ? (
                                        <>
                                            <HourglassEmptyIcon fontSize="small" /> Rejecting...
                                        </>
                                    ) : (
                                        <>
                                            <CancelIcon fontSize="small" /> Confirm Rejection
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>
                    )}
                    
                </ModalBody>
            </Modal>
        </>
    );
};

export default OneNotificationModal;
